import { FC } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import { Stack } from '@mui/material';
import DrawerContent from '../content';
import { useQuery } from 'react-query';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import NiceDate from '@/components/nice-date';
import NoneChip from '@/components/chip/none-chip';
import { QueryKey } from '@/lib/query-client';
import { AuditLogModel } from '@/lib/models/audit-log/audit-log.model';
import { getAuditLogById } from '@/lib/services/audit-log.service';
import CopyClipButton from '@/components/copy-clip-button';
import Chip from '@/components/chip';
import { startCase } from 'lodash';
import { DrawerProps } from '../helpers';
import AuditLogMenu from './menu';

interface Props extends DrawerProps {
  id: string;
}

const fields: ReadonlyField[] = [
  { label: 'Actor', prop: 'actor-section', propType: 'section' },
  { label: 'Name', prop: 'actor' },
  { label: 'Email', prop: 'email' },
  { label: 'Event', prop: 'event-section', propType: 'section' },
  { label: 'Type', prop: 'eventType' },
  { label: 'Action', prop: 'eventAction' },
  { label: 'Date', prop: 'eventDateTime' },
  { label: 'Message', prop: 'Details' },
  { label: 'Resource', prop: 'resource' },
];

const getReadonlyData = (data: AuditLogModel | null) => {
  if (!data) {
    return {};
  }

  const {
    eventType,
    eventAction,
    actionLabel,
    actionColor,
    eventDateTime,
    cleanMessage,
    actor,
    resourceLabel,
    resourceId,
  } = data;
  const { name, email } = actor;

  return {
    actor: name || <NoneChip system />,
    email,
    eventType: eventType ? startCase(eventType) : <NoneChip notAvailable />,
    eventAction: eventAction ? (
      <Chip label={actionLabel} color={actionColor} />
    ) : (
      <NoneChip notAvailable />
    ),
    eventDateTime: (
      <Stack direction="row" gap={1}>
        <NiceDate val={eventDateTime} withTime withOffset />
      </Stack>
    ),
    message: cleanMessage || <NoneChip notAvailable />,
    resource: resourceLabel ? (
      <Stack direction="row" gap={0.5}>
        {resourceLabel}
        <CopyClipButton value={resourceId} tooltip="Copy Resource ID" type="resource-id" />
      </Stack>
    ) : (
      <NoneChip notAvailable />
    ),
  };
};

export const AuditLogDrawer: FC<Props> = ({ id, open, onClose, className, isTopDrawer }) => {
  const query = useQuery([QueryKey.AuditLogView, id], async () => getAuditLogById(id));

  const { data = new AuditLogModel() } = query;

  if (!data) {
    return null;
  }

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query}>
      <DrawerHeader
        onClose={onClose}
        isTopDrawer={isTopDrawer}
        menu={<AuditLogMenu resourceId={data.resourceId} />}
      >
        Audit Log Details
      </DrawerHeader>
      <DrawerContent>
        <ReadonlyView fields={fields} data={getReadonlyData(data)} />
      </DrawerContent>
    </Drawer>
  );
};

export default AuditLogDrawer;
